var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var PipeConfig_1;
import { ToolType } from '@symon-ai/wisepipe-base-pipe';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { ExportConfig } from './exportConfig';
import { FileConfig } from './fileConfig';
import { Graph } from './graph';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationState } from './validation/toolValidationResult';
import { pipeConfigSchemaValidation } from '../validations.generated';
export class PipeDependantID {
    // pipeID - outerPipeID
    // elementID = outer pipe's pipeConfig elementID
    constructor(pipeID, elementID) {
        this.pipeID = pipeID;
        this.elementID = elementID;
    }
    toString() {
        return `${this.pipeID}_${this.elementID}`;
    }
}
export class PipeDependant {
    constructor(pipeID, graph, dependants) {
        this.pipeID = pipeID;
        this.graph = graph;
        this.dependants = dependants;
    }
}
export const pipeConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:PipeConfig',
    type: 'object',
    properties: {
        pipeID: { type: 'string', uid: 10 }
    },
    required: ['pipeID']
};
let PipeConfig = PipeConfig_1 = class PipeConfig extends ToolConfig {
    constructor(pipeID, customFormats) {
        super(ToolType.PIPE, customFormats);
        this.pipeID = pipeID;
        JSON.validate(this, PipeConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get topIn() {
        return PipeConfig_1.topIn;
    }
    // eslint-disable-next-line class-methods-use-this
    get bottomIn() {
        return PipeConfig_1.bottomIn;
    }
    // eslint-disable-next-line class-methods-use-this
    get topOut() {
        return PipeConfig_1.topOut;
    }
    // eslint-disable-next-line class-methods-use-this
    get bottomOut() {
        return PipeConfig_1.bottomOut;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return PipeConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return PipeConfig_1.outs;
    }
    static get topIn() {
        return PipeConfig_1.ins[0];
    }
    static get bottomIn() {
        return PipeConfig_1.ins[1];
    }
    static get topOut() {
        return PipeConfig_1.outs[0];
    }
    static get bottomOut() {
        return PipeConfig_1.outs[1];
    }
    static get ins() {
        return ['top', 'bottom'];
    }
    static get outs() {
        return ['top', 'bottom'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.PIPE.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.UNSEEN], this.ins, this.outs, ['pipe'], true);
    }
    getOutput(iConfigHelper, ins) {
        const inputNames = ins.names;
        const dependeeIns = ins.dependee.inputNodes(2);
        const dependeeOuts = ins.dependee.outputNodes(2);
        // replace scope within child pipe
        dependeeIns.forEach((input, index) => {
            if (inputNames.has(this.ins[index])) {
                const tsb = new ToolScopeBuilder(ins);
                tsb.export(FileConfig.out).copyAll(this.ins[index]);
                ins.dependeeMeta.setOutput(input.elementID, tsb.build());
            }
        });
        // get scope for each output
        const outs = dependeeOuts.map((output, index) => {
            const outputIns = ins.dependeeMeta.getMetadata(output.elementID).ins;
            const tsb = new ToolScopeBuilder(outputIns);
            tsb.setPipeID(ins.pipeID).setDependants(ins.dependants);
            tsb
                .export(this.outs[index])
                .copyAll(ExportConfig.in)
                .initializeColumnFormats()
                .setColumnFormats(this.customFormats);
            return tsb.build();
        });
        const tsb = new ToolScopeBuilder(...outs);
        return tsb.exportAll().build();
    }
    isValidForCalc(iConfigHelper, scope) {
        if (scope.pipeID === this.pipeID || scope.dependants.some((d) => d.pipeID === this.pipeID)) {
            return ToolValidationState.CYCLE;
        }
        const dependee = scope.dependee;
        const dependeeMeta = scope.dependeeMeta;
        if (!dependee || !dependeeMeta) {
            return ToolValidationState.BAD_METADATA;
        }
        const inputNames = scope.names;
        const dependeeIns = dependee.inputNodes(2);
        const dependeeOuts = dependee.outputNodes(2);
        if (dependeeOuts.length === 0) {
            return ToolValidationState.BAD_METADATA;
        }
        // replace scope within child pipe
        dependeeIns.forEach((input, index) => {
            if (inputNames.has(this.ins[index])) {
                const tsb = new ToolScopeBuilder(scope);
                tsb.export(FileConfig.out).copyAll(this.ins[index]);
                dependeeMeta.setOutput(input.elementID, tsb.build());
            }
        });
        // validate each output
        for (const output of dependeeOuts) {
            const subGraph = new Graph([...dependee.ancestors(output.elementID)]);
            for (const n of subGraph.getComputeOrder()) {
                const ins = dependeeMeta.getMetadata(n.elementID).ins;
                if (!n.config)
                    return ToolValidationState.EMPTY;
                const state = n.config.isValidForRun(iConfigHelper, ins, false);
                if (state !== ToolValidationState.VALID) {
                    return state;
                }
            }
        }
        return ToolValidationState.VALID;
    }
};
PipeConfig = PipeConfig_1 = __decorate([
    JsonSchemaV2(pipeConfigSchema, pipeConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.PIPE)
], PipeConfig);
export { PipeConfig };
//# sourceMappingURL=pipeConfig.js.map